import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { usePrefersReducedMotion } from "@ui/hooks/use-prefers-reduced-motion";
import { createComponentId, SheetStack } from "@ui/primitives/Sheet";
import { type ReactNode, use } from "react";

/**
 * The stack push-navigation Pages join (`Page.Root pushNavigation`) so the
 * page underneath parallaxes while they travel. One is enough: only one routed
 * page travels at a time.
 */
export const PAGE_STACK_SHEET_ID = createComponentId();

export interface PageStackRootProps {
  children: ReactNode;
}

/**
 * Establishes the push-navigation stack. Must contain BOTH the parallaxing
 * content (`PageStack.Outlet`) and wherever pushed Pages mount — Silk resolves
 * stack membership through React context, so a Page rendered outside this
 * subtree silently joins no stack and its parallax is dropped.
 *
 * In practice that means wrapping the routed-page outlet and the modal root
 * together, not just the page content.
 */
function PageStackRoot({ children }: PageStackRootProps) {
  return (
    <SheetStack.Root className="contents" componentId={PAGE_STACK_SHEET_ID}>
      {children}
    </SheetStack.Root>
  );
}

export interface PageStackOutletProps {
  children: ReactNode;
  className?: string;
}

/**
 * The content that recedes behind a pushed page: as the page travels in from
 * the right, this slides ~30% left behind it (the iOS navigation-push idiom),
 * driven 1:1 by travel progress. Desktop and reduced motion stay static — the
 * detached dialog presentation does not push.
 */
function PageStackOutlet({ children, className }: PageStackOutletProps) {
  const isDesktop = useBreakpoint("md");
  const reducedMotion = usePrefersReducedMotion();
  const parallax = isDesktop || reducedMotion ? undefined : { translateX: ["0px", "-30vw"] as [string, string] };

  // Silk resolves `forComponent` by reading the id as a React context and
  // falls back to an empty object, so an Outlet mounted outside its Root
  // animates nothing and reports no error. This is the only signal that the
  // wiring is wrong, and the previous arrangement went unnoticed because of it.
  if (use(PAGE_STACK_SHEET_ID) === null) {
    throw new Error("PageStack.Outlet must be rendered inside PageStack.Root");
  }

  return (
    <SheetStack.Outlet className={className} forComponent={PAGE_STACK_SHEET_ID} stackingAnimation={parallax}>
      {children}
    </SheetStack.Outlet>
  );
}

export const PageStack = {
  Outlet: PageStackOutlet,
  Root: PageStackRoot,
};
